// External dependencies
import { useMemo, useEffect, memo } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";

// Internal dependencies and styles
import "./App.css";
import { CONFIG } from "./config";
import AppRoutes from "./routes";
import { createAppTheme } from "./theme/appTheme";
import { useUrlStates } from "./hooks/useUrlStates";
import { useThemeManagement } from "./hooks/useThemeManagement";
import { useCollapsedState } from "./hooks/useCollapsedState";
import { useDataManagement } from "./hooks/useDataManagement";
import { useMobileDetection } from "./hooks/useMobileDetection";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import { setupAxiosAuthInterceptor } from "./API/axiosAuthInterceptor";

const MemoizedAppRoutes = memo(AppRoutes);

// Create a wrapper component to handle URL states
export default function App() {
  // Authentication and user context
  const { getAccessTokenSilently, user, isAuthenticated, isLoading } =
    useAuth0();

  // Setup Axios auth interceptor to handle token refresh on 401/403
  useEffect(() => {
    setupAxiosAuthInterceptor(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  // Initialize mobile detection globally
  useMobileDetection();

  // Redux state selectors
  const themeMode = useSelector((state) => state.themeMode);
  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );

  // Custom hooks for various app functionalities
  const { isShare } = useUrlStates();
  const { handleThemeChange } = useThemeManagement();
  const { collapsed, handleCollapsed } = useCollapsedState();

  // Create theme based on current theme mode
  const theme = useMemo(() => createAppTheme(themeMode, CONFIG), [themeMode]);

  // Set data-theme attribute on body for CSS targeting
  useEffect(() => {
    document.body.setAttribute("data-theme", themeMode);
  }, [themeMode]);

  // Setup data management
  useDataManagement(
    user,
    selectedOrganisation,
    isShare,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading
  );

  return (
    <ErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Layout
            handleThemeChange={handleThemeChange}
            handleCollapsed={handleCollapsed}
            collapsed={collapsed}
            isShare={isShare}
          >
            <MemoizedAppRoutes
              collapsed={collapsed}
              handleThemeChange={handleThemeChange}
              isShare={isShare}
            />
          </Layout>
        </ThemeProvider>
      </LocalizationProvider>
    </ErrorBoundary>
  );
}
